import { skipToken } from '@apollo/client/react'
import { getLocale } from 'src/stores/locale'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getLocalizedTackName } from 'src/utils/tack'
import { GetTackDocument, ListTacksDocument } from './generated/graphql'
import { createQueryHook, modifyQueryHook } from './utils'

export type NormalizedTagItem = {
  id: string
  name: string
  displayName?: string
  codeName?: string
  mediaId?: string
  thumbnailUrl?: string
  extra: { recommendType: string }
  createdAt: string
  updatedAt: string
}

export const useGetTack = modifyQueryHook(createQueryHook(GetTackDocument), {
  transform: (variables: {
    id?: string
    defaultName?: string
    codeName?: string
  }) => {
    if (!variables.id && !variables.defaultName && !variables.codeName) {
      return skipToken
    }
    return variables
  },
  reduce: r => r?.tack,
})

export const useListTacks = modifyQueryHook(
  createQueryHook(ListTacksDocument),
  {
    reduce: r => r?.tacks,
  },
)

/**
 * Get a single tack by codeName using the direct getTack query
 */
export const useGetTackByCodeName = (codeName: string | typeof skipToken) => {
  return useGetTack(codeName === skipToken ? skipToken : { codeName })
}

export const useListLocalizedTacks = modifyQueryHook(
  createQueryHook(ListTacksDocument),
  {
    reduce: (data): NormalizedTagItem[] =>
      getNodesFromConnection(data?.tacks).map(tack => {
        const localizedName = getLocalizedTackName(
          tack.tackTerms,
          tack.defaultName,
          getLocale(),
        )
        return {
          id: tack.id,
          name: localizedName,
          displayName: localizedName,
          codeName: tack.codeName,
          mediaId: tack.mediaId ?? undefined,
          extra: {
            recommendType:
              tack.category === 'general' ? 'feature' : tack.category,
          },
          createdAt: tack.createdAt,
          updatedAt: tack.updatedAt,
        }
      }),
  },
)
